import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  KeyboardShortcutRegistration,
  KeyboardShortcutsContextType,
} from '../../types/context';
import { getDomDepth } from './utils/getDomDepth';
import { describeKey } from './utils/describeKey';
import { debugLogger } from '../../debug/DebugLogger';

const log = debugLogger.scope('keyboardShortcuts');

const defaultValues: KeyboardShortcutsContextType = {
  isProvided: false,
  registerShortcut: () => {
    throw new Error('registerShortcut must be implemented');
  },
  attachShortcutWindow: () => {
    throw new Error('attachShortcutWindow must be implemented');
  },
};

export const KeyboardShortcutsContext =
  createContext<KeyboardShortcutsContextType>(defaultValues);

const KeyboardShortcutsContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  // Active shortcut entries keyed by id; mutated in place so registration is non-rendering.
  const registrationsRef = useRef(
    new Map<string, KeyboardShortcutRegistration>()
  );
  // Registration order list used as a fallback tiebreak when no scopeRef matches the last interaction.
  const orderRef = useRef<string[]>([]);
  // Most recent DOM target the user touched; read at keydown time to route the event by scope.
  const lastInteractedTargetRef = useRef<EventTarget | null>(null);

  // Adds a shortcut to the registry and returns its cleanup; re-registering the same id moves it to the end of the order list.
  const registerShortcut = useCallback(
    (registration: KeyboardShortcutRegistration) => {
      registrationsRef.current.set(registration.id, registration);
      orderRef.current = [
        ...orderRef.current.filter((id) => id !== registration.id),
        registration.id,
      ];

      /** @debugFeature keyboardShortcuts Logs a registered shortcut: its id and whether it dispatches in the capture phase. */
      log('shortcut registered', () => ({
        id: registration.id,
        capture: Boolean(registration.capture),
      }));

      return () => {
        registrationsRef.current.delete(registration.id);
        orderRef.current = orderRef.current.filter(
          (id) => id !== registration.id
        );

        /** @debugFeature keyboardShortcuts Logs an unregistered shortcut — it no longer reacts to keys. */
        log('shortcut unregistered', () => ({ id: registration.id }));
      };
    },
    []
  );

  // Remember the last element the user pointed at or focused; the keydown handler routes the shortcut by it.
  const markInteraction = useCallback((event: Event) => {
    lastInteractedTargetRef.current = event.target;
  }, []);

  const onKeyDown = useCallback((event: KeyboardEvent) => {
    // Collect every registration whose shouldHandle accepts this event — these are the dispatch candidates.
    const candidates: KeyboardShortcutRegistration[] = [];
    for (const id of orderRef.current) {
      const registration = registrationsRef.current.get(id);
      if (
        registration &&
        !registration.capture &&
        registration.shouldHandle(event)
      ) {
        candidates.push(registration);
      }
    }
    if (candidates.length === 0) return;

    // Narrow candidates to those whose scopeRef DOM root contains the last interaction.
    const interacted = lastInteractedTargetRef.current as Node | null;
    const scoped = interacted
      ? candidates.filter((r) => r.scopeRef?.current?.contains(interacted))
      : [];

    // No scope matched the cursor: fall back to the most recently registered shortcut.
    let picked = candidates[candidates.length - 1];
    // Otherwise the innermost scope under the cursor wins (a nested slave grid beats its master).
    if (scoped.length > 0) {
      picked = scoped.reduce((deepest, current) => {
        if (
          getDomDepth(current.scopeRef!.current) >
          getDomDepth(deepest.scopeRef!.current)
        ) {
          return current;
        }
        return deepest;
      });
    }

    /** @debugFeature keyboardShortcuts Logs a bubble-phase dispatch: the key combo, all candidate ids, the ids whose scope contains the last interaction, the picked shortcut and whether it won by scope or by the most-recently-registered fallback. */
    log('shortcut dispatched', () => ({
      key: describeKey(event),
      candidates: candidates.map((r) => r.id),
      scoped: scoped.map((r) => r.id),
      picked: picked.id,
      pickedBy: scoped.length > 0 ? 'innermost-scope' : 'last-registered',
    }));

    picked.onTrigger(event);
  }, []);

  // Capture-phase registrations pre-empt component-internal key handling (React root handlers run later in the bubble phase). Routed by event.target — for keydown that is the focused element — because these shortcuts are focus-anchored, unlike the bubble path which follows the pointer.
  const onKeyDownCapture = useCallback((event: KeyboardEvent) => {
    const target = event.target as Node | null;
    if (!target) return;

    const candidates: KeyboardShortcutRegistration[] = [];
    for (const id of orderRef.current) {
      const registration = registrationsRef.current.get(id);
      if (
        registration &&
        registration.capture &&
        registration.scopeRef?.current?.contains(target) &&
        registration.shouldHandle(event)
      ) {
        candidates.push(registration);
      }
    }
    if (candidates.length === 0) return;

    // The deepest scope containing the focused target wins (a nested slave grid beats its master).
    const picked = candidates.reduce((deepest, current) => {
      if (
        getDomDepth(current.scopeRef!.current) >
        getDomDepth(deepest.scopeRef!.current)
      ) {
        return current;
      }
      return deepest;
    });

    /** @debugFeature keyboardShortcuts Logs a capture-phase dispatch: the key combo, candidate ids and the picked shortcut — the event is swallowed even when its onTrigger decides to no-op. */
    log('capture shortcut dispatched', () => ({
      key: describeKey(event),
      candidates: candidates.map((r) => r.id),
      picked: picked.id,
    }));

    // Swallow before triggering: a matched capture shortcut owns the event even when onTrigger decides to no-op, so the pre-empted component handler never runs.
    event.preventDefault();
    event.stopPropagation();
    picked.onTrigger(event);
  }, []);

  // Attaches the full dispatcher (interaction tracking + keydown routing) to a
  // window; used for the main window and any extra one (canvas PiP window,
  // which has its own event loop the main listeners never see).
  const attachShortcutWindow = useCallback(
    (targetWindow: Window) => {
      /** @debugFeature keyboardShortcuts Logs the shortcut dispatcher attaching to a window (the main window, or an extra one like the pop-out editor with its own event loop). */
      log('shortcut window attached', () => ({
        isMainWindow: targetWindow === window,
      }));

      targetWindow.addEventListener('mousemove', markInteraction, true);
      targetWindow.addEventListener('mousedown', markInteraction, true);
      targetWindow.addEventListener('focusin', markInteraction, true);
      targetWindow.addEventListener('touchstart', markInteraction, true);
      // Bubble phase: pre-empted by useModalKeyboard's capture-phase listener while a modal is open.
      targetWindow.addEventListener('keydown', onKeyDown);
      targetWindow.addEventListener('keydown', onKeyDownCapture, true);

      return () => {
        targetWindow.removeEventListener('mousemove', markInteraction, true);
        targetWindow.removeEventListener('mousedown', markInteraction, true);
        targetWindow.removeEventListener('focusin', markInteraction, true);
        targetWindow.removeEventListener('touchstart', markInteraction, true);
        targetWindow.removeEventListener('keydown', onKeyDown);
        targetWindow.removeEventListener('keydown', onKeyDownCapture, true);
      };
    },
    [markInteraction, onKeyDown, onKeyDownCapture]
  );

  useEffect(() => attachShortcutWindow(window), [attachShortcutWindow]);

  const value: KeyboardShortcutsContextType = useMemo(
    () => ({
      isProvided: true,
      registerShortcut,
      attachShortcutWindow,
    }),
    [registerShortcut, attachShortcutWindow]
  );

  return (
    <KeyboardShortcutsContext.Provider value={value}>
      {children}
    </KeyboardShortcutsContext.Provider>
  );
};

export default KeyboardShortcutsContextProvider;
